import { useState, useEffect, useCallback, Dispatch, SetStateAction } from "react"
import {
  saveEncryptedFormState,
  loadEncryptedFormState,
  clearFormPersistence,
} from "../utils/formCrypto"

export interface UseFormPersistenceOptions<T> {
  /**
   * Unique key to identify this form's persisted state
   */
  formKey: string
  /**
   * Initial state to use when no persisted state exists
   */
  initialState: T
}

export interface UseFormPersistenceReturn<T> {
  /**
   * Current form state
   */
  formState: T
  /**
   * Update form state (same signature as React's setState)
   */
  setFormState: Dispatch<SetStateAction<T>>
  /**
   * Save current form state to encrypted storage (call before auth redirect)
   */
  saveFormState: () => Promise<void>
  /**
   * Clear persisted state for this form
   */
  clearPersistedState: () => void
  /**
   * True while restoring state from storage
   */
  isRestoring: boolean
}

/**
 * Hook for managing encrypted form state persistence.
 *
 * Use this hook to persist form state across page reloads (e.g., during OAuth flows).
 * Form data is encrypted using AES-GCM before being stored in sessionStorage.
 *
 * @example
 * ```tsx
 * const { formState, setFormState, saveFormState, isRestoring } = useFormPersistence({
 *   formKey: "feedbackForm",
 *   initialState: { name: "", email: "" },
 * });
 *
 * // Before auth redirect:
 * const handleAuthenticate = async () => {
 *   await saveFormState();
 *   authClient.signinRedirect();
 * };
 * ```
 */
export function useFormPersistence<T>({
  formKey,
  initialState,
}: UseFormPersistenceOptions<T>): UseFormPersistenceReturn<T> {
  const [formState, setFormState] = useState<T>(initialState)
  const [isRestoring, setIsRestoring] = useState(true)

  // Restore encrypted state on mount
  useEffect(() => {
    let mounted = true

    const restoreState = async () => {
      try {
        const savedState = await loadEncryptedFormState<T>(formKey)
        if (savedState && mounted) {
          console.log(`[useFormPersistence] Restored encrypted state for ${formKey}`)
          setFormState(savedState)
          // Clear persisted state after successful restore
          clearFormPersistence(formKey)
        }
      } catch (error) {
        console.error(`[useFormPersistence] Error restoring state for ${formKey}:`, error)
      } finally {
        if (mounted) {
          setIsRestoring(false)
        }
      }
    }

    restoreState()

    return () => {
      mounted = false
    }
  }, [formKey])

  // Save form state to encrypted storage
  const saveFormState = useCallback(async () => {
    try {
      await saveEncryptedFormState(formKey, formState)
      console.log(`[useFormPersistence] Saved encrypted state for ${formKey}`)
    } catch (error) {
      console.error(`[useFormPersistence] Error saving state for ${formKey}:`, error)
      // Continue without persistence - don't block the auth flow
    }
  }, [formKey, formState])

  // Clear persisted state
  const clearPersistedState = useCallback(() => {
    clearFormPersistence(formKey)
  }, [formKey])

  return {
    formState,
    setFormState,
    saveFormState,
    clearPersistedState,
    isRestoring,
  }
}
